import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useGetList, useNotify, useTranslate } from "ra-core";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { STAGE_CRITERIA_REGISTRY } from "../deals/stageConfig";
import { STAGE_CRITERION_CODES } from "../types";
import type { DealStageCriterion, StageCriterionCode } from "../types";
import { toStageConfigErrorMessage } from "./stageConfigForm";

/**
 * Almikia Epic EP (TASK-105): "Criterios para avanzar" -- one toggle per
 * registry criterion (`STAGE_CRITERION_CODES`, `posi_complete` only in EP).
 * Turning a criterion OFF is a risky change (a lead can then enter the stage
 * without the 4 POSI answers), so it asks for a plain-Spanish confirmation
 * first (design-system principle: friction proportional to risk); turning
 * one ON needs no confirmation. Nothing is ever deleted: an existing row's
 * `enabled` flips, or a new disabled/enabled row is inserted when none
 * exists yet (DATA CONTRACT EP (2)).
 */
export const StageCriteriaSection = ({ stageKey }: { stageKey: string }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [pendingCode, setPendingCode] = useState<StageCriterionCode | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: criteria,
    isPending,
    refetch,
  } = useGetList<DealStageCriterion>("deal_stage_criteria", {
    filter: { stage_key: stageKey },
    pagination: { page: 1, perPage: 10 },
  });

  if (isPending || !criteria) {
    return <Skeleton className="h-11 w-full" />;
  }

  const rowByCode = new Map(
    criteria.map((criterion) => [criterion.criterion_code, criterion]),
  );

  const setEnabled = async (code: StageCriterionCode, enabled: boolean) => {
    setIsSaving(true);
    try {
      const existing = rowByCode.get(code);
      if (existing) {
        await dataProvider.update("deal_stage_criteria", {
          id: existing.id,
          data: { enabled },
          previousData: existing,
        });
      } else {
        await dataProvider.create("deal_stage_criteria", {
          data: { stage_key: stageKey, criterion_code: code, enabled },
        });
      }
      await refetch();
      // Almikia Epic EP: see DealStageConfigCreate.tsx -- toggling a
      // criterion changes a stage's readiness, so the effective-stages
      // cache read by `useConfigurationLoader` must be busted too (react-admin
      // invalidation does not apply here since these are plain dataProvider
      // calls, not a react-admin mutation).
      queryClient.invalidateQueries({
        queryKey: ["deal_stage_configs", "effective"],
      });
    } catch (error) {
      notify(toStageConfigErrorMessage(error), { type: "error" });
    } finally {
      setIsSaving(false);
      setPendingCode(null);
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-muted-foreground">
        {translate("resources.deal_stage_configs.criteria.title")}
      </h3>
      {STAGE_CRITERION_CODES.map((code) => {
        const definition = STAGE_CRITERIA_REGISTRY[code];
        const enabled = rowByCode.get(code)?.enabled ?? false;
        return (
          <div
            key={code}
            className="flex items-center justify-between gap-3 rounded-md border p-3"
          >
            <span className="text-sm">{definition.label}</span>
            <Button
              type="button"
              size="sm"
              variant={enabled ? "default" : "outline"}
              className="min-h-11 shrink-0"
              disabled={isSaving}
              onClick={() =>
                enabled ? setPendingCode(code) : setEnabled(code, true)
              }
            >
              {enabled
                ? translate("resources.deal_stage_configs.criteria.enabled")
                : translate("resources.deal_stage_configs.criteria.disabled")}
            </Button>
          </div>
        );
      })}
      <Confirm
        isOpen={pendingCode != null}
        loading={isSaving}
        title="resources.deal_stage_configs.criteria.confirm_off_title"
        content="resources.deal_stage_configs.criteria.confirm_off_body"
        confirm="resources.deal_stage_configs.criteria.confirm_off_action"
        confirmColor="warning"
        onConfirm={() => pendingCode && setEnabled(pendingCode, false)}
        onClose={() => setPendingCode(null)}
      />
    </div>
  );
};
